import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { money2, errMsg } from "./payrollHelpers";

const FIELD_HINTS = {
  regularHours: "Regular Hours",
  otHours: "OT Hours",
  ptoHours: "PTO Hours",
  sickHours: "Sick Hours",
  holidayHours: "Holiday Hours",
};

export default function EditPayrollTimeModal({ employeeId, employeeName, defaultDate, hourlyRate, focusField, onClose, onSubmit }) {
  const [date, setDate] = useState(defaultDate || new Date().toISOString().slice(0, 10));
  const [regularHours, setRegularHours] = useState("");
  const [otHours, setOtHours] = useState("");
  const [ptoHours, setPtoHours] = useState("");
  const [sickHours, setSickHours] = useState("");
  const [holidayHours, setHolidayHours] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const n = (v) => { const f = parseFloat(v); return Number.isNaN(f) ? 0 : f; };
  const hasRate = hourlyRate > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error("A reason is required");
    const entries = [];
    const hoursDelta = n(regularHours) + n(otHours);
    if (hoursDelta !== 0) entries.push({ employeeId, category: "TIME", adjustmentAmount: hoursDelta, date, reason: reason.trim(), notes: notes.trim() });
    [["PTO", ptoHours], ["SICK", sickHours], ["HOLIDAY", holidayHours]].forEach(([cat, val]) => {
      const hrs = n(val);
      if (hrs === 0) return;
      const dollars = Math.round(hrs * (hourlyRate || 0) * 100) / 100;
      const note = `${notes.trim() ? notes.trim() + " — " : ""}${hrs} hrs @ ${money2(hourlyRate || 0)}/hr`;
      entries.push({ employeeId, category: cat, adjustmentAmount: dollars, date, reason: reason.trim(), notes: note, hours: hrs });
    });
    if (entries.length === 0) return toast.error("Enter at least one hour amount");
    setBusy(true);
    try {
      await onSubmit(entries);
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Failed to save time correction"));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, value, setValue) => (
    <div>
      <label className={`block text-[12px] font-semibold mb-1 ${focusField === key ? "text-blue-700" : "text-slate-700"}`}>{label}</label>
      <input
        type="number" step="0.25" {...(key === "ptoHours" || key === "sickHours" || key === "holidayHours" ? { min: "0" } : {})} value={value} onChange={(e) => setValue(e.target.value)}
        autoFocus={focusField === key}
        data-testid={`time-correction-${key}`}
        className={`w-full h-9 px-2.5 rounded-md border text-[13px] ${focusField === key ? "border-blue-400 ring-1 ring-blue-200" : "border-slate-200"}`}
        placeholder="0"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200" data-testid="edit-payroll-time-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">Edit Payroll Time {employeeName ? `— ${employeeName}` : ""}</div>
          <button type="button" onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="time-correction-date" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {field("regularHours", FIELD_HINTS.regularHours, regularHours, setRegularHours)}
            {field("otHours", FIELD_HINTS.otHours, otHours, setOtHours)}
            {field("ptoHours", FIELD_HINTS.ptoHours, ptoHours, setPtoHours)}
            {field("sickHours", FIELD_HINTS.sickHours, sickHours, setSickHours)}
            {field("holidayHours", FIELD_HINTS.holidayHours, holidayHours, setHolidayHours)}
          </div>
          {!hasRate && (ptoHours || sickHours || holidayHours) && (
            <div className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
              This employee has no hourly rate on file — PTO/Sick/Holiday hours will be recorded at $0. Use "Add Adjustment" for a direct dollar amount instead.
            </div>
          )}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Reason <span className="text-rose-500">*</span></label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="time-correction-reason" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" placeholder="e.g. Missed clock-in on job site" />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Notes</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-2.5 py-2 rounded-md border border-slate-200 text-[13px] resize-none" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-slate-200 text-[13px] font-semibold">Cancel</button>
          <button type="submit" disabled={busy} data-testid="time-correction-submit-btn" className="h-9 px-3 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[13px] font-semibold">{busy ? "Saving…" : "Save Adjustment"}</button>
        </div>
      </form>
    </div>
  );
}
